<template>
  <div class="location-integration-settings">
    <ui-loading
      v-if="isLoading || isRyftLoading"
      inline
      class="location-integration-settings__loading"
    />

    <template v-else>
      <div
        v-for="integration in integrationsSettingsConfig"
        :key="integration.info[0].value"
        class="location-integration-settings__integration-wrapper"
      >
        <div class="location-integration-settings__integration">
          <!-- Integration info -->
          <div class="location-integration-settings__integration-data">
            <div
              v-for="info in integration.info"
              :key="info.value"
              class="location-integration-settings__integration-info"
            >
              <div class="location-integration-settings__integration-label">
                {{ info.label }}
              </div>

              <div class="location-integration-settings__integration-value">
                {{ info.value || '—' }}
              </div>
            </div>
          </div>

          <template v-if="integration.modules">
            <div
              v-for="module in integration.modules"
              :key="module.value"
              class="location-integration-settings__integration-module"
            >
              <div class="location-integration-settings__integration-module-name">
                {{ module.name }}
              </div>

              <div
                v-for="moduleInfo in module.data"
                :key="moduleInfo.value"
                class="location-integration-settings__integration-module-info"
              >
                <div class="location-integration-settings__integration-label">
                  {{ moduleInfo.label }}
                </div>

                <div
                  class="location-integration-settings__integration-value"
                  :style="{
                    color: moduleInfo.color,
                  }"
                >
                  {{ moduleInfo.value || '—' }}
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <div
        v-if="ryftPosDetails"
        class="location-integration-settings__integration-wrapper"
      >
        <div class="location-integration-settings__integration">
          <div class="location-integration-settings__integration-data">
            <div
              v-for="ryftPosDetail in ryftPosDetailsList"
              :key="ryftPosDetail.label"
              class="location-integration-settings__integration-info"
            >
              <div class="location-integration-settings__integration-label">
                {{ ryftPosDetail.label }}
              </div>

              <div
                class="location-integration-settings__integration-value"
                :class="ryftPosDetail.status ? `location-integration-settings__integration-value--${ryftPosDetail.status}`  : ''"
              >
                {{ ryftPosDetail.value || '—' }}
              </div>
            </div>
          </div>
          <div v-if="ryftPosDetails.terminals.length">
            <div class="location-integration-settings__ryft-terminals-table-header location-integration-settings__ryft-terminals-table-row">
              <div>Ryft Terminal ID</div>
              <div>POS serial number</div>
              <div>Status</div>
            </div>

            <div
              v-for="terminal in ryftPosDetails.terminals"
              :key="terminal.id"
              class="location-integration-settings__ryft-terminals-table-row"
            >
              <div>{{ terminal.ryftTerminalId }}</div>
              <div>{{ terminal.serialNumber }}</div>
              <div>
                <ui-icon
                  v-if="terminal.status === 'ACTIVATED' || terminal.status === 'WAITING_FOR_ACTIVATION'"
                  name="check-rounded-success"
                  size="12"
                />
                <ui-icon
                  v-else-if="terminal.status === 'FAILED'"
                  name="cross-rounded-error"
                  size="12"
                />
                <template v-else>
                  {{ terminal.status }}
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { formatDate } from '@fullcalendar/core';
import api from '@/api';
import { INTEGRATION_LABEL, INTEGRATION_STATUS_CONFIG } from '@/config/integrations';

export default {
  name: 'LocationIntegrationSettings',

  props: {
    location: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      isLoading: false,
      isRyftLoading: false,
      integrationSettings: null,
      ryftPosDetails: null,
    };
  },

  computed: {
    integrationsSettingsConfig() {
      if (!this.integrationSettings) {
        return null;
      }

      return this.integrationSettings?.map((integration) => {
        return {
          info: [
            {
              label: this.$t('integrations.name'),
              value: INTEGRATION_LABEL[integration.integrationSystem],
            },
            {
              label: this.$t(`integrations.${integration.integrationSystem}.externalIdLabel`),
              value: integration.externalId,
            },
          ],
          modules: integration.modules?.map((module) => ({
            name: this.$t(`integrations.${integration.integrationSystem}.module.${module.type}.title`),
            data: [
              {
                label: this.$t('integrations.createdDate'),
                value: formatDate(module.integrationCreated),
              },
              {
                label: this.$t('integrations.endDate'),
                value: formatDate(module.integrationStopped),
              },
              {
                label: this.$t('integrations.status'),
                value: INTEGRATION_STATUS_CONFIG[module.status]?.label,
                color: INTEGRATION_STATUS_CONFIG[module.status]?.color,
              },
            ],
          })) || [],
        };
      });
    },
    ryftPosDetailsList() {
      if (!this.ryftPosDetails) {
        return null;
      }

      return [
        {
          label: 'Integration name',
          value: this.ryftPosDetails.name,
        },
        {
          label: 'Ryft Location ID',
          value: this.ryftPosDetails.ryftLocationId,
        },
        {
          label: 'Integration created',
          value: formatDate(this.ryftPosDetails.createdDate),
        },
        {
          label: 'Integration stopped',
          value: formatDate(this.ryftPosDetails.stoppedDate),
        },
        (() => {
          if (this.ryftPosDetails.status === 'CONNECTED') {
            return ({
              label: 'Status',
              value: 'Connected',
              status: 'success',
            });
          }

          if (this.ryftPosDetails.status === 'DISCONNECTED') {
            return ({
              label: 'Status',
              value: 'Disconnected',
              status: 'error',
            });
          }

          return ({
            label: 'Status',
            value: this.ryftPosDetails.status,
          });
        })(),
      ];
    },
  },

  methods: {
    async getRyftPosSettings(ryftId) {
      try {
        this.isRyftLoading = true;

        const { data } = await api.locations.getRyftPosDetails(this.location.id, ryftId);

        this.ryftPosDetails = data;
      } catch (e) {
        this.$showServerError(e);
      } finally {
        this.isRyftLoading = false;
      }
    },
    async getIntegrationSettings() {
      try {
        this.isLoading = true;

        const { data } = await api.locations.getIntegrationSettingsConfig(this.location.id);
        this.integrationSettings = data.filter((item) => item.integrationSystem !== 'RYFT_POS');

        const ryftId = data.find((item) => item.integrationSystem === 'RYFT_POS')?.id || null;

        if (ryftId) {
          this.getRyftPosSettings(ryftId);
        }
      } catch (e) {
        this.$showServerError(e);
      } finally {
        this.isLoading = false;
      }
    },
  },

  mounted() {
    this.getIntegrationSettings();
  },
};
</script>

<style lang="scss">
.location-integration-settings {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 80px;

  &__loading {
    margin: auto;
  }

  &__integration {
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: min-content;

    > * + * {
      border-top: 1px solid #CCCCCC;
      padding-top: 20px;
    }

    &-label,
    &-value {
      font-size: 14px;
      line-height: 24px;
      font-weight: 400;
    }

    &-value {
      &--success {
        color: #23BC85;
      }

      &--error {
        color: #FD4346;
      }
    }

    &-label {
      opacity: 0.5;
    }

    &-data,
    &-module {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    &-info,
    &-module-info {
      display: flex;
      justify-content: space-between;
      align-items: center;
      min-width: 300px;
      gap: 20px;
    }

    &-info:nth-child(1) div {
      font-weight: 700;
    }

    &-module {
      &-name {
        font-size: 14px;
        line-height: 24px;
        font-weight: 700;
      }
    }
  }

  &__ryft-terminals-table {
    &-header {
      color: #808080;
    }

    &-row {
      display: grid;
      grid-template-columns: 260px 120px 1fr;
      gap: 10px;
      width: 100%;
      padding-bottom: 15px;
      margin-top: 15px;
      border-bottom: 1px solid #CCCCCC;

      & > :nth-child(1) {
        justify-self: start;
      }

      & > :nth-child(2) {
        justify-self: center;
      }

      & > :nth-child(3) {
        justify-self: end;
      }
    }
  }
}
</style>
